import React, { useState, useEffect } from 'react';
import './LeavePopup.css';
import { firestore, auth } from '../firebase';
import { collection, addDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import API_BASE_URL from '../config';

const LeavePopup = ({ onClose }) => {
    const [formData, setFormData] = useState({
        fullName: '',
        startDate: '',
        endDate: '',
        startTime: '',
        endTime: '',
        leaveType: '',
        reason: ''
    });
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showTime, setShowTime] = useState(false);
    const { user } = useAuth();

    // Reset End Date if Start Date changes to be after it
    useEffect(() => {
        if (formData.startDate && formData.endDate && formData.endDate < formData.startDate) {
            setFormData(prev => ({ ...prev, endDate: '' }));
        }
    }, [formData.startDate]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        // Real-time validation for End Date
        if (name === 'endDate' && formData.startDate && value < formData.startDate) {
            // Do not update state if invalid date is selected
            return;
        }

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            // 1. Save to Firestore
            const user = auth.currentUser;
            if (user) {
                await addDoc(collection(firestore, "leaves"), {
                    userId: user.uid,
                    fullName: formData.fullName,
                    email: user.email,
                    startDate: formData.startDate,
                    startTime: formData.startTime,
                    endDate: formData.endDate,
                    endTime: formData.endTime,
                    leaveType: formData.leaveType,
                    reason: formData.reason,
                    status: 'Pending',
                    createdAt: new Date().toISOString()
                });
            } else {
                console.warn("No authenticated user found when submitting leave.");
            }

            // 2. Send Email to Admin (Backend)
            const response = await fetch(`${API_BASE_URL}/send-leave`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ ...formData, email: user.email }),
            });

            if (response.ok) {
                setIsSubmitted(true);
                setTimeout(() => {
                    onClose();
                }, 3000);
            } else {
                const errorData = await response.json();
                console.error("Backend error:", errorData);
                alert(`Error: ${errorData.error || 'Failed to submit leave request'}`);
            }
        } catch (error) {
            console.error("Network error:", error);
            alert("Could not connect to server. Ensure the backend is running.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    // Get today's date for min start date
    const today = new Date().toISOString().split('T')[0];

    return (
        <div className="leave-popup-overlay" onClick={handleOverlayClick}>
            <div className="leave-popup-content">
                <button className="close-btn" onClick={onClose} aria-label="Close">
                    &times;
                </button>

                {!isSubmitted ? (
                    <>
                        <div className="leave-popup-header">
                            <h2>Leave Application</h2>
                            <p style={{ color: '#888', marginTop: '0.5rem' }}>Submit your leave request to Admin.</p>
                        </div>

                        <form className="leave-popup-form" onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label htmlFor="fullName">Full Name</label>
                                <input
                                    type="text"
                                    id="fullName"
                                    name="fullName"
                                    value={formData.fullName}
                                    onChange={handleChange}
                                    required
                                    placeholder="John Doe"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="leaveType">Leave Type</label>
                                <select
                                    id="leaveType"
                                    name="leaveType"
                                    value={formData.leaveType}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="" disabled>Select Leave Type</option>
                                    <option value="Annual Leave">Annual Leave</option>
                                    <option value="Parental Leave (Unpaid)">Parental Leave (Unpaid)</option>
                                    <option value="Personal (Sick/Carer’s) Leave">Personal (Sick/Carer’s) Leave</option>
                                    <option value="Carer’s Leave (Unpaid)">Carer’s Leave (Unpaid)</option>
                                    <option value="Compassionate Leave (Unpaid)">Compassionate Leave (Unpaid)</option>
                                    <option value="Community Service Leave">Community Service Leave</option>
                                    <option value="Other Unpaid Leave">Other Unpaid Leave</option>
                                </select>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div className="form-group">
                                    <label htmlFor="startDate">Start Date</label>
                                    <input
                                        type="date"
                                        id="startDate"
                                        name="startDate"
                                        value={formData.startDate} // Expects YYYY-MM-DD
                                        onChange={handleChange}
                                        required
                                        min={today}
                                        style={{ colorScheme: 'dark' }} // Attempt to fix calendar icon visibility in dark mode if needed
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="endDate" style={{ color: !formData.startDate ? '#666' : 'inherit' }}>
                                        End Date
                                    </label>
                                    <input
                                        type="date"
                                        id="endDate"
                                        name="endDate"
                                        value={formData.endDate}
                                        onChange={handleChange}
                                        required
                                        min={formData.startDate || today}
                                        disabled={!formData.startDate}
                                        title={!formData.startDate ? "Please select a Start Date first" : ""}
                                        style={{
                                            cursor: !formData.startDate ? 'not-allowed' : 'pointer',
                                            opacity: !formData.startDate ? 0.5 : 1
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="form-group" style={{ marginBottom: '1.5rem', marginTop: '0.5rem' }}>
                                <span
                                    onClick={() => setShowTime(!showTime)}
                                    style={{
                                        cursor: 'pointer',
                                        fontWeight: '500',
                                        color: showTime ? '#f59e0b' : '#aaa',
                                        whiteSpace: 'nowrap',
                                        fontSize: '0.9rem',
                                        display: 'block',
                                        textAlign: 'center',
                                        padding: '12px 16px',
                                        borderRadius: '8px',
                                        background: '#2a2a2a',
                                        border: showTime ? '1px solid #f59e0b' : '1px solid #333',
                                        transition: 'all 0.3s ease',
                                        boxShadow: showTime ? '0 0 10px rgba(245, 158, 11, 0.1)' : 'none'
                                    }}
                                >
                                    {showTime ? '✓ Specific Time Added (Click to Remove)' : 'Add Specific Time (Optional)'}
                                </span>
                            </div>

                            {showTime && (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                                    <div className="form-group">
                                        <label htmlFor="startTime">Start Time</label>
                                        <input
                                            type="time"
                                            id="startTime"
                                            name="startTime"
                                            value={formData.startTime}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="endTime">End Time</label>
                                        <input
                                            type="time"
                                            id="endTime"
                                            name="endTime"
                                            value={formData.endTime}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="form-group">
                                <label htmlFor="reason">Reason / Description</label>
                                <textarea
                                    id="reason"
                                    name="reason"
                                    value={formData.reason}
                                    onChange={handleChange}
                                    required
                                    placeholder="Tell us why you're applying for leave"
                                />
                            </div>

                            <p className="leave-note" style={{ fontSize: '0.85rem', color: '#888', fontStyle: 'italic', marginBottom: '1rem' }}>
                                “Within 7 days prior — no active job/leave accumulated and not in breach of employment conditions.”
                            </p>

                            <button type="submit" className="submit-btn" disabled={isLoading}>
                                {isLoading ? 'Submitting...' : 'Submit Leave Request'}
                            </button>
                        </form>
                    </>
                ) : (
                    <div className="success-message">
                        <span className="success-icon">&#10003;</span>
                        <h3 style={{ color: '#fff' }}>Application Sent</h3>
                        <p style={{ color: '#aaa', marginTop: '1rem' }}>
                            Your leave request has been sent to Admin (MNET).
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LeavePopup;
